import type { HydratedImage } from "../generated/api/types.gen";
import { trackEvent } from "./analytics";
import { api } from "./api-client";

export type DownloadScale = 1 | 2 | 4;

/** Filename for a downloaded design. `index` is the 1-based position within a
 *  session, included only where the user is browsing a set (the gallery). */
export function buildDownloadFilename(
  image: HydratedImage,
  scale: DownloadScale,
  index?: number,
): string {
  const subject = image.design_subject || "design";
  const position = index === undefined ? "" : `-${index}`;
  const suffix = scale > 1 ? `-${scale}x` : "";
  return `${subject}${position}${suffix}.png`;
}

/** Fetch the image at the requested scale and hand it to the browser as a
 *  download. Throws on failure so callers own their own error surface. */
export async function downloadDesignImage(
  image: HydratedImage,
  scale: DownloadScale,
  index?: number,
): Promise<void> {
  const response =
    scale === 1
      ? await api.images.download(image.id)
      : await api.images.downloadUpscaled(image.id, scale);

  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = buildDownloadFilename(image, scale, index);
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
  if (scale > 1) {
    trackEvent("upscale_downloaded", { scale });
  }
}
